import {
  DEFAULT_SERVER_PAGE_SIZE,
  serverPageSizeFrom,
  startingRecNoFor,
} from "../../utils/offsetPaging";
import { serverPageArrowsProps } from "../../utils/serverPaging";
import ServerPageArrows from "../../common/ServerPageArrows";
import {
  Button,
  Column,
  DataTable,
  Dropdown,
  Form,
  Grid,
  Heading,
  Modal,
  Pagination,
  Search,
  Section,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectRow,
  TextInput,
} from "@carbon/react";
import React, { useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertDialog,
  NotificationKinds,
} from "../../common/CustomNotification";
import PageBreadCrumb from "../../common/PageBreadCrumb";
import { ConfigurationContext, NotificationContext } from "../../layout/Layout";
import "../../Style.css";
import {
  getFromOpenElisServer,
  postToOpenElisServer,
  postToOpenElisServerFullResponse,
} from "../../utils/Utils";

function DictionaryManagement() {
  const intl = useIntl();
  const componentMounted = useRef(false);
  const dirtyFieldsRef = useRef(new Set());

  const { notificationVisible, setNotificationVisible, addNotification } =
    useContext(NotificationContext);
  const { reloadConfiguration } = useContext(ConfigurationContext);
  const [dictionaryMenuList, setDictionaryMenuList] = useState([]);

  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  const [categoryDescription, setCategoryDescription] = useState([]);

  const [category, setCategory] = useState("");
  const [dictionaryNumber, setDictionaryNumber] = useState("");
  const [dictionaryEntry, setDictionaryEntry] = useState("");
  const [localAbbreviation, setLocalAbbreviation] = useState("");
  const [containerPopulation, setContainerPopulation] = useState("");
  const [isActive, setIsActive] = useState("");
  const [loincCode, setLoincCode] = useState("");

  const [fromRecordCount, setFromRecordCount] = useState("1");
  const [toRecordCount, setToRecordCount] = useState("");
  const [totalRecordCount, setTotalRecordCount] = useState("");
  const [serverPageSize, setServerPageSize] = useState(
    DEFAULT_SERVER_PAGE_SIZE,
  );
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [modifyButton, setModifyButton] = useState(true);
  const [deactivateButton, setDeactivateButton] = useState(true);
  const [editMode, setEditMode] = useState(true);

  const startingRecNo = startingRecNoFor(page, serverPageSize);
  const [panelSearchTerm, setPanelSearchTerm] = useState("");
  const [searchedMenuList, setSearchedMenuList] = useState([]);
  const isSearching = Boolean(panelSearchTerm);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 530);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 530);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Browse and search are separate response snapshots. When search is cleared,
  // the now-visible browse page is fetched instead of exposing the copy that
  // was held before a dictionary write.
  useEffect(() => {
    componentMounted.current = true;
    if (panelSearchTerm) {
      getFromOpenElisServer(
        `/rest/SearchDictionaryMenu?search=Y&startingRecNo=${startingRecNo}&searchString=${panelSearchTerm}`,
        fetchedSearchedDictionaryMenu,
      );
    } else {
      setSearchedMenuList([]);
      getFromOpenElisServer(
        `/rest/DictionaryMenu?startingRecNo=${startingRecNo}`,
        fetchedDictionaryMenu,
      );
    }
    return () => {
      componentMounted.current = false;
    };
  }, [panelSearchTerm, startingRecNo]);

  useEffect(() => {
    if (selectedRowIds.length === 1) {
      setModifyButton(false);
    } else {
      setModifyButton(true);
    }
    if (selectedRowIds.length === 0) {
      setDeactivateButton(true);
    } else {
      setDeactivateButton(false);
    }
  }, [selectedRowIds]);

  const yesOrNo = [
    {
      id: "Y",
      value: "Y",
    },
    {
      id: "N",
      value: "N",
    },
  ];

  const handlePageChange = ({ page: newPage }) => {
    if (newPage !== page) {
      setPage(newPage);
      setSelectedRowIds([]);
    }
  };
  const arrows = serverPageArrowsProps({
    paging: {
      currentPage: page,
      totalPages: Math.max(
        Math.ceil((Number(totalRecordCount) || 0) / serverPageSize),
        1,
      ),
    },
    onPageRequest: (pageNumber) => handlePageChange({ page: pageNumber }),
  });

  const fetchedDictionaryMenu = (res) => {
    if (componentMounted.current) {
      if (res) {
        if (
          res.toRecordCount !== undefined &&
          res.fromRecordCount !== undefined &&
          res.totalRecordCount !== undefined
        ) {
          setToRecordCount(res.toRecordCount);
          setFromRecordCount(res.fromRecordCount);
          setTotalRecordCount(res.totalRecordCount);
          setServerPageSize((previous) =>
            serverPageSizeFrom(
              res.fromRecordCount,
              res.toRecordCount,
              res.totalRecordCount,
              previous,
            ),
          );
        }
        if (res.menuList) {
          const menuList = res.menuList.map((item) => ({
            id: item.id,
            dictEntry: item.dictEntry,
            localAbbreviation: item.localAbbreviation,
            isActive: item.isActive,
            loincCode: item.loincCode || "",
            categoryName: item.dictionaryCategory
              ? item.dictionaryCategory.categoryName
              : "not available",
            lastupdated: item.lastupdated,
          }));
          setDictionaryMenuList(menuList);
        }
      }
    }
  };

  const fetchedDictionaryCategory = (category) => {
    if (componentMounted.current) {
      setCategoryDescription(category);
    }
  };

  const fetchedSearchedDictionaryMenu = (res) => {
    if (componentMounted.current) {
      if (res) {
        if (
          res.toRecordCount !== undefined &&
          res.fromRecordCount !== undefined &&
          res.totalRecordCount !== undefined
        ) {
          setToRecordCount(res.toRecordCount);
          setFromRecordCount(res.fromRecordCount);
          setTotalRecordCount(res.totalRecordCount);
          setServerPageSize((previous) =>
            serverPageSizeFrom(
              res.fromRecordCount,
              res.toRecordCount,
              res.totalRecordCount,
              previous,
            ),
          );
        }
        if (res.menuList) {
          const menuList = res.menuList.map((item) => ({
            id: item.id,
            dictEntry: item.dictEntry,
            localAbbreviation: item.localAbbreviation,
            isActive: item.isActive,
            loincCode: item.loincCode || "",
            categoryName: item.dictionaryCategory
              ? item.dictionaryCategory.categoryName
              : "not available",
            lastupdated: item.lastupdated,
          }));
          setSearchedMenuList(menuList);
        }
      }
    }
  };

  useEffect(() => {
    componentMounted.current = true;
    getFromOpenElisServer(
      "/rest/dictionary-categories",
      fetchedDictionaryCategory,
    );
    return () => {
      componentMounted.current = false;
    };
  }, []);

  /**
   * Rereads whichever list is on screen: the search results if a search
   * term is active, the paged browse list otherwise.
   */
  const refreshDictionaryList = () => {
    if (panelSearchTerm) {
      getFromOpenElisServer(
        `/rest/SearchDictionaryMenu?search=Y&startingRecNo=${startingRecNo}&searchString=${panelSearchTerm}`,
        fetchedSearchedDictionaryMenu,
      );
    } else {
      getFromOpenElisServer(
        `/rest/DictionaryMenu?startingRecNo=${startingRecNo}`,
        fetchedDictionaryMenu,
      );
    }
  };

  const postData = {
    id: dictionaryNumber,
    selectedDictionaryCategoryId: category?.id,
    dictEntry: dictionaryEntry,
    localAbbreviation: localAbbreviation,
    containerPopulation: containerPopulation || null,
    isActive: isActive.id,
    loincCode: loincCode.trim() || null,
    dirtyFormFields: "",
  };

  async function displayStatus(res) {
    setNotificationVisible(true);
    if (res.status == "201" || res.status == "200") {
      addNotification({
        kind: NotificationKinds.success,
        title: intl.formatMessage({ id: "notification.title" }),
        message: intl.formatMessage({ id: "success.add.edited.msg" }),
      });
    } else {
      addNotification({
        kind: NotificationKinds.error,
        title: intl.formatMessage({ id: "notification.title" }),
        message: intl.formatMessage({ id: "error.add.edited.msg" }),
      });
    }
    if (res.status == "201" || res.status == "200") {
      setSelectedRowIds([]);
      refreshDictionaryList();
    }
  }

  const handleSubmitModal = (e) => {
    e.preventDefault();
    postToOpenElisServerFullResponse(
      "/rest/Dictionary",
      JSON.stringify(postData),
      displayStatus,
    );
    setOpen(false);
  };

  const handleUpdateModal = (e) => {
    e.preventDefault();

    if (!componentMounted.current[dictionaryEntry]) {
      dirtyFieldsRef.current.add("dictEntry");
    }

    if (!componentMounted.current[isActive]) {
      dirtyFieldsRef.current.add("isActive");
    }

    if (!componentMounted.current[localAbbreviation]) {
      dirtyFieldsRef.current.add("localAbbreviation");
    }

    const dirtyFields =
      dirtyFieldsRef.current.size > 0
        ? `;${[...dirtyFieldsRef.current].join(";")}`
        : "";

    const updateData = {
      id: dictionaryNumber,
      selectedDictionaryCategoryId: category.id,
      dictEntry: dictionaryEntry,
      localAbbreviation: localAbbreviation,
      containerPopulation: containerPopulation || null,
      isActive: isActive.id,
      loincCode: loincCode.trim() || null,
      dirtyFormFields: dirtyFields,
    };

    postToOpenElisServerFullResponse(
      `/rest/Dictionary?ID=${selectedRowIds[0]}&startingRecNo=${startingRecNo}`,
      JSON.stringify(updateData),
      displayStatus,
    );
    setOpen(false);
  };

  const renderCell = (cell, row) => {
    if (cell.info.header === "select") {
      return (
        <TableSelectRow
          key={cell.id}
          id={cell.id}
          checked={selectedRowIds.includes(row.id)}
          name="selectRowRadio"
          ariaLabel="selectRow"
          onSelect={(e) => {
            e.stopPropagation();
            if (selectedRowIds.includes(row.id)) {
              setSelectedRowIds(selectedRowIds.filter((id) => id !== row.id));
            } else {
              setSelectedRowIds([...selectedRowIds, row.id]);
            }
          }}
        />
      );
    } else if (
      cell.info.header === "value" &&
      typeof cell.value === "string" &&
      cell.value.startsWith("data:image")
    ) {
      return (
        <TableCell key={cell.id}>
          <img
            src={cell.value}
            alt="Config Image"
            style={{ maxWidth: "50px" }}
          />
        </TableCell>
      );
    }
    return (
      <TableCell key={cell.id} data-cy={`cell-${cell.info.header}-${row.id}`}>
        {cell.value}
      </TableCell>
    );
  };

  const handleDictionaryMenuItems = (res) => {
    if (componentMounted.current) {
      setDictionaryNumber(res.id);
      setCategory(res.dictionaryCategory);
      setDictionaryEntry(res.dictEntry);
      setIsActive(yesOrNo.find((item) => item.id === res.isActive));
      setLocalAbbreviation(res.localAbbreviation);
      setContainerPopulation(res.containerPopulation || "");
      setLoincCode(res.loincCode || "");
    }
  };

  const handleOnClickOnModification = async (event) => {
    event.preventDefault();
    if (selectedRowIds.length == 1) {
      const selectedItem = dictionaryMenuList.find(
        (item) => item.id === selectedRowIds[0],
      );

      if (selectedItem) {
        setDictionaryNumber(selectedItem.id);
        setCategory(selectedItem.category);
        setDictionaryEntry(selectedItem.dictEntry);
        setLocalAbbreviation(selectedItem.localAbbreviation);
        setIsActive(yesOrNo.find((item) => item.id === selectedItem.isActive));
        setLoincCode(selectedItem.loincCode);
        setOpen(true);
        setEditMode(false);
      }

      getFromOpenElisServer(
        `/rest/Dictionary?ID=${selectedRowIds[0]}&startingRecNo=${startingRecNo}`,
        handleDictionaryMenuItems,
      );
      setOpen(true);
      setEditMode(false);
    }
  };

  const handleDeactivation = async (event) => {
    event.preventDefault();
    if (selectedRowIds.length > 0) {
      postToOpenElisServer(
        `/rest/DeleteDictionary?ID=${selectedRowIds.join(",")}`,
        {},
        handleDelete,
      );
    }
  };

  const handleDelete = (status) => {
    setNotificationVisible(true);
    if (status == "200") {
      addNotification({
        kind: NotificationKinds.success,
        title: intl.formatMessage({ id: "notification.title" }),
        message: intl.formatMessage({
          id: "dictionary.menu.deactivate.success",
        }),
      });
      setSelectedRowIds([]);
      reloadConfiguration();
    } else {
      addNotification({
        kind: NotificationKinds.error,
        title: intl.formatMessage({ id: "notification.title" }),
        message: intl.formatMessage({ id: "dictionary.menu.deactivate.fail" }),
      });
    }
    refreshDictionaryList();
  };

  const handlePanelSearchChange = (event) => {
    const query = event.target.value;
    setPanelSearchTerm(query);
    setPage(1);
  };

  return (
    <div className="adminPageContent">
      {notificationVisible === true ? <AlertDialog /> : ""}
      <PageBreadCrumb
        breadcrumbs={[
          { label: "home.label", link: "/" },
          { label: "breadcrums.admin.managment", link: "/MasterListsPage" },
          {
            label: "dictionary.label.modify",
            link: "/MasterListsPage/DictionaryMenu",
          },
        ]}
      />
      <Grid fullWidth={true}>
        <Column lg={16} md={8} sm={4}>
          <Section>
            <Heading>
              <FormattedMessage id="dictionary.label.modify" />
            </Heading>
          </Section>
          <br />
          <Section>
            <Form
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "1rem" : "2rem",
                justifyContent: "space-between",
                alignItems: isMobile ? "stretch" : "center",
                flexWrap: "wrap",
              }}
            >
              <Column
                lg={16}
                md={8}
                sm={4}
                style={{
                  display: "flex",
                  gap: isMobile ? "0.75rem" : "0.5rem",
                  flexDirection: isMobile ? "column" : "row",
                  width: isMobile ? "100%" : "auto",
                  margin: "0",
                }}
              >
                <Button
                  data-cy="addButton"
                  style={{ width: isMobile ? "100%" : "auto" }}
                  disabled={!editMode}
                  onClick={() => setOpen(true)}
                >
                  {intl.formatMessage({
                    id: "admin.page.configuration.formEntryConfigMenu.button.add",
                  })}
                </Button>
                <Button
                  data-cy="modifyButton"
                  style={{ width: isMobile ? "100%" : "auto" }}
                  disabled={modifyButton}
                  type="submit"
                  onClick={handleOnClickOnModification}
                >
                  <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.button.modify" />
                </Button>
                <Modal
                  open={open}
                  size="sm"
                  onRequestClose={() => setOpen(false)}
                  modalHeading={
                    editMode
                      ? intl.formatMessage({
                          id: "dictionary.modal.add.heading",
                        })
                      : intl.formatMessage({
                          id: "dictionary.modal.edit.heading",
                        })
                  }
                  primaryButtonText={
                    editMode
                      ? intl.formatMessage({ id: "label.button.add" })
                      : intl.formatMessage({ id: "label.button.update" })
                  }
                  secondaryButtonText={intl.formatMessage({
                    id: "label.button.cancel",
                  })}
                  onRequestSubmit={
                    editMode ? handleSubmitModal : handleUpdateModal
                  }
                >
                  <TextInput
                    data-modal-primary-focus
                    id="dictNumber"
                    labelText={intl.formatMessage({
                      id: "dictionary.number.label",
                    })}
                    disabled
                    value={dictionaryNumber}
                    onChange={(e) => setDictionaryNumber(e.target.value)}
                    style={{
                      marginBottom: "1rem",
                    }}
                  />
                  <Dropdown
                    id="description"
                    label=""
                    type="default"
                    items={categoryDescription}
                    titleText={intl.formatMessage({
                      id: "dictionary.category.label",
                    })}
                    itemToString={(item) => (item ? item.description : "")}
                    onChange={({ selectedItem }) => {
                      setCategory(selectedItem);
                      if (selectedItem?.categoryName !== "Sample Container")
                        setContainerPopulation("");
                    }}
                    selectedItem={category}
                    size="md"
                    style={{
                      marginBottom: "1rem",
                    }}
                  />
                  {category?.categoryName === "Sample Container" && (
                    <Dropdown
                      id="container-population"
                      titleText={intl.formatMessage({
                        id: "dictionary.containerPopulation",
                      })}
                      label={intl.formatMessage({
                        id: "dictionary.containerPopulation.unspecified",
                      })}
                      items={["", "ADULT", "PAEDIATRIC"]}
                      selectedItem={containerPopulation}
                      itemToString={(item) =>
                        intl.formatMessage({
                          id: `dictionary.containerPopulation.${item || "unspecified"}`,
                        })
                      }
                      onChange={({ selectedItem }) => {
                        setContainerPopulation(selectedItem || "");
                        dirtyFieldsRef.current.add("containerPopulation");
                      }}
                    />
                  )}
                  <TextInput
                    id="dictEntry"
                    labelText={intl.formatMessage({
                      id: "dictionary.dictEntry",
                    })}
                    value={dictionaryEntry}
                    onChange={(e) => setDictionaryEntry(e.target.value)}
                    style={{
                      marginBottom: "1rem",
                    }}
                  />
                  <Dropdown
                    id="isActive"
                    type="default"
                    label=""
                    items={yesOrNo}
                    titleText={intl.formatMessage({
                      id: "dictionary.category.isActive",
                    })}
                    itemToString={(item) => (item ? item.id : "")}
                    onChange={({ selectedItem }) => {
                      setIsActive(selectedItem);
                    }}
                    selectedItem={isActive}
                    size="md"
                    style={{
                      marginBottom: "1rem",
                    }}
                  />
                  <TextInput
                    id="localAbbrev"
                    labelText={intl.formatMessage({
                      id: "dictionary.category.localAbbreviation",
                    })}
                    value={localAbbreviation}
                    onChange={(e) => setLocalAbbreviation(e.target.value)}
                    style={{
                      marginBottom: "1rem",
                    }}
                  />

                  <TextInput
                    id="loincCode"
                    labelText={intl.formatMessage({
                      id: "dictionary.loincCode",
                    })}
                    value={loincCode}
                    onChange={(e) => setLoincCode(e.target.value)}
                    // invalid={!/^(?!-)(?:\d+-)*\d*$/.test(loincCode)}
                    // invalidText={
                    //   <FormattedMessage id="dictionary.loincCode.invalid" />
                    // }
                    style={{
                      marginBottom: "1rem",
                    }}
                  />
                </Modal>
                <Button
                  data-cy="deactivateButton"
                  style={{ width: isMobile ? "100%" : "auto" }}
                  disabled={deactivateButton}
                  onClick={handleDeactivation}
                  type="submit"
                >
                  <FormattedMessage id="admin.page.configuration.formEntryConfigMenu.button.deactivate" />
                </Button>
              </Column>

              <Column
                lg={16}
                md={8}
                sm={4}
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isMobile ? "0.75rem" : "0.5rem",
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "1.2rem" : "1.2rem",
                    textAlign: isMobile ? "center" : "left",
                  }}
                >
                  <FormattedMessage id="showing" /> {fromRecordCount} -{" "}
                  {toRecordCount} <FormattedMessage id="of" />{" "}
                  {totalRecordCount}
                </h4>
              </Column>
            </Form>
          </Section>
        </Column>
      </Grid>
      <div className="orderLegendBody">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Section>
              <Search
                size="lg"
                id="dictionary-entry-search"
                labelText={<FormattedMessage id="search.by.dictionary.entry" />}
                placeholder={intl.formatMessage({
                  id: "search.by.dictionary.entry",
                })}
                onChange={handlePanelSearchChange}
                value={panelSearchTerm || ""}
              ></Search>
            </Section>
          </Column>
        </Grid>
        <br />
        <Grid fullWidth={true} className="gridBoundary">
          <Column lg={16} md={8} sm={4}>
            {arrows.show && <ServerPageArrows {...arrows} />}
            <DataTable
              size="sm"
              rows={isSearching ? searchedMenuList : dictionaryMenuList}
              headers={[
                {
                  key: "select",
                  header: intl.formatMessage({
                    id: "admin.page.configuration.formEntryConfigMenu.select",
                  }),
                },
                {
                  key: "categoryName",
                  header: intl.formatMessage({
                    id: "dictionary.category.name",
                  }),
                },
                {
                  key: "dictEntry",
                  header: intl.formatMessage({ id: "dictionary.dictEntry" }),
                },
                {
                  key: "localAbbreviation",
                  header: intl.formatMessage({
                    id: "dictionary.category.localAbbreviation",
                  }),
                },
                {
                  key: "isActive",
                  header: intl.formatMessage({
                    id: "dictionary.category.isActive",
                  }),
                },

                {
                  key: "loincCode",
                  header: intl.formatMessage({ id: "dictionary.loincCode" }),
                },
              ]}
              isSortable
            >
              {({ rows, headers, getHeaderProps, getTableProps }) => {
                return (
                  <TableContainer title="" description="">
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map((header) => (
                            <TableHeader
                              key={header.key}
                              {...getHeaderProps({ header })}
                            >
                              {header.header}
                            </TableHeader>
                          ))}
                          <TableHeader />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.id}>
                            {row.cells.map((cell) => renderCell(cell, row))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              }}
            </DataTable>
            <Pagination
              onChange={handlePageChange}
              page={page}
              pageSize={serverPageSize}
              pageSizes={[serverPageSize]}
              pageSizeInputDisabled
              totalItems={Number(totalRecordCount) || 0}
              forwardText={intl.formatMessage({ id: "pagination.forward" })}
              backwardText={intl.formatMessage({ id: "pagination.backward" })}
              size="sm"
              itemRangeText={(min, max, total) =>
                intl.formatMessage(
                  { id: "pagination.item-range" },
                  { min: min, max: max, total: total },
                )
              }
              itemsPerPageText={intl.formatMessage({
                id: "pagination.items-per-page",
              })}
              itemText={(min, max) =>
                intl.formatMessage(
                  { id: "pagination.item" },
                  { min: min, max: max },
                )
              }
              pageNumberText={intl.formatMessage({
                id: "pagination.page-number",
              })}
              pageRangeText={(_current, total) =>
                intl.formatMessage(
                  { id: "pagination.page-range" },
                  { total: total },
                )
              }
              pageText={(page, pagesUnknown) =>
                intl.formatMessage(
                  { id: "pagination.page" },
                  { page: pagesUnknown ? "" : page },
                )
              }
            />
          </Column>
        </Grid>
      </div>
    </div>
  );
}

export default DictionaryManagement;
